import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  Plus,
  RefreshCw,
  Download,
  ChevronDown,
  Database,
  History,
  Undo2,
  FileText,
  Sparkles,
  Layers,
  Wrench,
} from 'lucide-react';
import type { BulkScrapeScope } from '../../utils/bulkScrapeReport';

interface Props {
  bulkScrapeScope: BulkScrapeScope;
  onBulkScrapeScopeChange: (scope: BulkScrapeScope) => void;
  bulkScraping: boolean;
  updatingDescriptions: boolean;
  loading: boolean;
  onBulkScrape: () => void;
  onUpdateDescriptions: () => void;
  onEstablishBaselines: () => void;
  onFillSnapshots: () => void;
  onRollback: () => void;
  onExportCsv: () => void;
  onExportTxt: () => void;
  onAddForum: () => void;
}

const menuPanel =
  'absolute right-0 z-20 mt-1.5 min-w-[220px] origin-top-right rounded-xl border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-gray-600 dark:bg-gray-800';

function menuItemClass(active: boolean) {
  return `flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
    active
      ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
      : 'text-gray-700 dark:text-gray-200'
  } disabled:opacity-50 disabled:pointer-events-none`;
}

const ForumListToolbar: React.FC<Props> = ({
  bulkScrapeScope,
  onBulkScrapeScopeChange,
  bulkScraping,
  updatingDescriptions,
  loading,
  onBulkScrape,
  onUpdateDescriptions,
  onEstablishBaselines,
  onFillSnapshots,
  onRollback,
  onExportCsv,
  onExportTxt,
  onAddForum,
}) => {
  return (
    <div className="mb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white shrink-0">Forumlar</h2>

        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-xs font-medium shadow-sm"
            title="Toplu güncelleme kapsamı"
          >
            <button
              type="button"
              onClick={() => onBulkScrapeScopeChange('active')}
              disabled={bulkScraping}
              className={`px-3 py-2 transition-colors ${
                bulkScrapeScope === 'active'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              } disabled:opacity-50`}
            >
              Aktif
            </button>
            <button
              type="button"
              onClick={() => onBulkScrapeScopeChange('all')}
              disabled={bulkScraping}
              className={`px-3 py-2 border-l border-gray-200 dark:border-gray-600 transition-colors ${
                bulkScrapeScope === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              } disabled:opacity-50`}
            >
              Tümü
            </button>
          </div>

          <button
            type="button"
            onClick={onBulkScrape}
            disabled={bulkScraping}
            title="Seçicilerden okur, önizleme gösterir"
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-teal-600 to-teal-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-teal-700 hover:to-teal-600 disabled:opacity-50 transition-all"
          >
            <RefreshCw size={16} className={bulkScraping ? 'animate-spin' : ''} />
            {bulkScraping ? 'Güncelleniyor…' : 'Toplu güncelle'}
          </button>

          <Menu as="div" className="relative">
            <Menu.Button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
              <Wrench size={16} className="text-gray-500 dark:text-gray-400" />
              Bakım
              <ChevronDown size={14} className="opacity-60" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className={menuPanel}>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      className={menuItemClass(active)}
                      onClick={onUpdateDescriptions}
                      disabled={updatingDescriptions}
                    >
                      <Sparkles size={16} className="text-purple-500 shrink-0" />
                      {updatingDescriptions ? 'Açıklamalar güncelleniyor…' : 'Açıklamaları güncelle'}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      className={menuItemClass(active)}
                      onClick={onEstablishBaselines}
                      disabled={loading || bulkScraping}
                    >
                      <Layers size={16} className="text-amber-500 shrink-0" />
                      Önceki ay bazını oluştur
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button type="button" className={menuItemClass(active)} onClick={onFillSnapshots}>
                      <History size={16} className="text-violet-500 shrink-0" />
                      Geçmiş kayıtları doldur
                    </button>
                  )}
                </Menu.Item>
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                <Menu.Item>
                  {({ active }) => (
                    <button type="button" className={menuItemClass(active)} onClick={onRollback}>
                      <Undo2 size={16} className="text-orange-500 shrink-0" />
                      Son toplu güncellemeyi geri al
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative">
            <Menu.Button className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
              <Download size={16} className="text-gray-500 dark:text-gray-400" />
              İndir
              <ChevronDown size={14} className="opacity-60" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className={menuPanel}>
                <Menu.Item>
                  {({ active }) => (
                    <button type="button" className={menuItemClass(active)} onClick={onExportCsv}>
                      <Database size={16} className="text-green-600 shrink-0" />
                      CSV indir
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button type="button" className={menuItemClass(active)} onClick={onExportTxt}>
                      <FileText size={16} className="text-blue-600 shrink-0" />
                      TXT indir
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>

          <button
            type="button"
            onClick={onAddForum}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:from-indigo-700 hover:to-indigo-600 hover:shadow-lg transition-all"
          >
            <Plus size={18} strokeWidth={2.5} />
            Yeni forum
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForumListToolbar;
